import { Viewport } from 'pixi-viewport';
import { findIndex, includes } from 'lodash';
import i18n from '@/i18n';
import ImageCrops from './ImageCrops';
import SceneImage from './SceneImage';
import bus, { EVENTS } from './bus';
import Mask from './Mask';

export default class ReportInspectorScene extends PIXI.Application {
  constructor(options, mosaic = '', imageSets, imageResults, report, skus, selectedImageNumber, isReversed = false, params) {
    super({
      ...options,
      autoResize: true,
      transparent: true
    });

    this.mosaic = mosaic;
    this.imageSets = imageSets;
    this.imageResults = imageResults;
    this.report = report;
    this.skuList = skus;

    this.bgAlpha = params.bgAlpha;
    this.imageSetId = params.imageSetId;
    this.reference = params.reference;
    this.sceneZoomScaleValue = params.sceneZoomScaleValue;

    this.sceneTitle = params.sceneTitle;

    if (this.reference) {
      this.referenceImages = params.referenceImages;
    }

    this.selectedImageNumber = selectedImageNumber;
    this.isReversed = isReversed;
    this.stage.interactive = true;
  }

  async setupMaterials() {
    try {
      this.addViewport();
      const getImageSet = this.imageSets.find(x => x.image_set_id === this.imageSetId);
      await this.addSceneImage(getImageSet);
      this.listenEvents();

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  addViewport() {
    this.viewport = new Viewport({
      screenWidth: this.screen.width,
      screenHeight: this.screen.height,
      stopPropagation: true,
      passiveWheel: false,
      disableOnContextMenu: true,
      divWheel: this.renderer.view,
      interaction: new PIXI.InteractionManager(this.renderer)
    }).drag({
      mouseButtons: 'left'
    }).pinch()
      .wheel();

    this.viewport.clampZoom({
      maxScale: 2,
      minScale: 0
    });

    bus.viewport = this.viewport;

    this.stage.addChild(this.viewport);
  }

  async addSceneImage(imageSet) {
    let rectifiedImages = [];

    const mergedList = imageSet.images.map(image => ({ ...image, ...this.imageResults.find(result => result.image_id === image.image_id) }));
    const activeList = mergedList.filter(item => item.is_active === true);

    let referenceImageHeight = null;

    if (this.reference) {
      const img = await this.getMeta(this.referenceImages[0].file_urls.original);
      referenceImageHeight = img.height;
    }

    if (includes(bus.imageSetTypes, imageSet.type)) {
      const selectedImage = [activeList.find(item => item.position === this.selectedImageNumber)];

      rectifiedImages = this.getRectifiedImages(selectedImage, referenceImageHeight);
    } else {
      rectifiedImages = this.getRectifiedImages(activeList, referenceImageHeight);
    }

    if (rectifiedImages.length > 1) {
      rectifiedImages.forEach((acc) => {
        acc.interactive = true;
        bus.$on(EVENTS.SHOW_TOOL_TIP, ({ crop: { crop } }) => {
          if (acc.crops.some(c => c.crop_id === crop.crop_id)) {
            const childrenLength = acc.parent.children.filter(c => c.type === 'SceneImage').length;
            acc.parent.setChildIndex(acc, childrenLength - 1);
          }
        });
      });
    }

    const promises = rectifiedImages.map(mosaic => mosaic.setTexture());
    await Promise.all(promises);

    this.viewport.addChild(...rectifiedImages);

    this.moveCenter();

    if (includes(bus.imageSetTypes, imageSet.type)) {
      const selectedImage = activeList.find(item => item.position === this.selectedImageNumber);
      this.addImageCrops(selectedImage, referenceImageHeight);
    } else {
      // eslint-disable-next-line no-restricted-syntax
      for (const image of activeList) {
        this.addImageCrops(image, referenceImageHeight);
      }
    }

    this.addMask(this.bgAlpha);

    return Promise.resolve();
  }

  moveCenter() {
    if (this.stage) {
      bus.realogramSceneFitScaleValue = this.viewport.findFit(this.viewport.worldWidth, this.viewport.worldHeight).toFixed(2);
      const selectedScene = this.reference ? bus.sceneZoomScaleValue.planogram : bus.sceneZoomScaleValue.realogram;
      const viewportScale = this.viewport.height / this.viewport.width;

      if (viewportScale >= 1) {
        const isZoomAtPoint4 = selectedScene === 0.4 || (this.reference && bus.sceneZoomScaleValue.planogram === 0.4);
        const isDragging = ((bus.dragPositionForRealogram.x !== 0 && bus.dragPositionForRealogram.y) || (bus.dragPositionForPlanogram.x !== 0 && bus.dragPositionForPlanogram.y !== 0));

        if (isZoomAtPoint4) {
          this.viewport
            .moveCenter(this.viewport.width / 2, this.viewport.height / 2)
            .fit(true, this.viewport.width, this.viewport.height);
        } else if (isDragging) {
          this.setZoomAndLocation();
        }
      } else {
        const isZoomAtPoint4 = (selectedScene === 0.4 || (this.reference && bus.sceneZoomScaleValue.planogram === 0.4));
        const isDragging = ((bus.dragPositionForRealogram.x !== 0 && bus.dragPositionForRealogram.y) || (bus.dragPositionForPlanogram.x !== 0 && bus.dragPositionForPlanogram.y !== 0));

        if (isZoomAtPoint4) {
          this.viewport
            .fitHeight(this.viewport.height, true)
            .moveCorner(0, 0);
        } else if (isDragging) {
          this.setZoomAndLocation();
        }
      }
    }
  }

  addImageCrops(image, referenceImageHeight) {
    const imageCrops = new ImageCrops(
      image.crops,
      this.viewport,
      this.skuList,
      this.isReversed,
      { reference: this.reference, referenceImageHeight, global_boundaries_available: !!image.global_matrix_values }
    );
    imageCrops.type = 'ImageCrops';
    this.viewport.addChild(imageCrops);
  }

  setZoomAndLocation() {
    if (bus.dragPositionForRealogram.x !== 0 && bus.dragPositionForRealogram.y !== 0 && !this.reference) {
      this.viewport
        .fitHeight(this.viewport.height, true)
        .animate({
          time: 0.01,
          position: { x: bus.dragPositionForRealogram.x, y: bus.dragPositionForRealogram.y },
          scale: this.reference ? bus.sceneZoomScaleValue.planogram : bus.sceneZoomScaleValue.realogram
        });
    } else if (bus.dragPositionForPlanogram.x !== 0 && bus.dragPositionForPlanogram.y !== 0 && this.reference) {
      this.viewport
        .fitHeight(this.viewport.height, true)
        .animate({
          time: 0.01,
          position: { x: bus.dragPositionForPlanogram.x, y: bus.dragPositionForPlanogram.y },
          scale: this.reference ? bus.sceneZoomScaleValue.planogram : bus.sceneZoomScaleValue.realogram
        });
    }
  }

  // eslint-disable-next-line
  getMeta(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject();
      img.src = url;
    });
  }

  addMask(alpha) {
    let color = 0x000000;
    let bgAlpha = alpha;

    if (alpha < 0.5) {
      color = 0xffffff;
      bgAlpha = 1 - alpha;
    }

    if (alpha === 0.5) {
      bgAlpha = 0;
    }

    const { x, y } = this.viewport.getLocalBounds();

    const sceneMask = new Mask(
      bgAlpha,
      this.viewport.worldWidth,
      this.viewport.worldHeight,
      color,
      { x, y }
    );
    this.mask = sceneMask;

    this.viewport.addChildAt(this.mask, findIndex(this.viewport.children, child => child.type === 'ImageCrops'));
  }

  getRectifiedImages(activeList, referenceImageHeight) {
    return activeList.map((image, index) => {
      let rectificationMatrixValues = [];

      if (image.rectification_matrix_values) {
        rectificationMatrixValues = image.rectification_matrix_values;
      } else if (image.rectification_matrix) {
        rectificationMatrixValues = image.rectification_matrix.split(',');
      }

      return new SceneImage(
        this.reference ? this.referenceImages[index].file_urls.original : image.url,
        image.camera_view,
        {
          global_matrix_values: image.global_matrix_values ? image.global_matrix_values : [],
          rectification_matrix_values: rectificationMatrixValues
        },
        image.crops,
        this.isReversed,
        {
          reference: this.reference,
          referenceImageHeight,
          global_boundaries_available: image.crops.filter(crop => Object.prototype.hasOwnProperty.call(crop, 'global_boundaries')).length > 0
        }
      );
    });
  }

  // eslint-disable-next-line class-methods-use-this
  isSpriteVisible(sprite, viewportBounds) {
    const cropWidth = sprite.global_boundaries.bottom_right.x - sprite.global_boundaries.top_left.x;
    const cropHeight = sprite.global_boundaries.bottom_right.y - sprite.global_boundaries.top_left.y;
    const middle_x = sprite.global_boundaries.top_left.x + (sprite.global_boundaries.bottom_right.x - sprite.global_boundaries.top_left.x) / 2;
    const middle_y = sprite.global_boundaries.top_left.y + (sprite.global_boundaries.bottom_right.y - sprite.global_boundaries.top_left.y) / 2;

    return (
      middle_x + cropWidth >= viewportBounds.x
      && middle_x <= viewportBounds.x + viewportBounds.width
      && middle_y + cropHeight >= viewportBounds.y
      && middle_y <= viewportBounds.y + viewportBounds.height
    );
  }

  listenEvents = () => {
    this.viewport.on('zoomed-end', (event) => {
      if (this.reference && bus.selectedScene === i18n.t('Planogram')) {
        bus.sceneZoomScaleValue.planogram = this.viewport.scaled;

        bus.dragPositionForPlanogram.x = event.center.x;
        bus.dragPositionForPlanogram.y = event.center.y;
      } else if (!this.reference && bus.selectedScene === i18n.t('Realogram')) {
        bus.sceneZoomScaleValue.realogram = this.viewport.scaled;

        bus.dragPositionForRealogram.x = event.center.x;
        bus.dragPositionForRealogram.y = event.center.y;
      }

      bus.$emit(EVENTS.ZOOM_END);
    });

    this.viewport.on('drag-end', (event) => {
      if (this.reference) {
        bus.dragPositionForPlanogram.x = event.viewport.center.x;
        bus.dragPositionForPlanogram.y = event.viewport.center.y;
      } else {
        bus.dragPositionForRealogram.x = event.viewport.center.x;
        bus.dragPositionForRealogram.y = event.viewport.center.y;
      }
    });

    bus.$on(EVENTS.ZOOM_BUTTON_CHANGE, () => {
      if (this.reference && bus.selectedScene === i18n.t('Planogram')) {
        this.viewport.setZoom(bus.sceneZoomScaleValue.planogram, false);
      } else if (!this.reference && bus.selectedScene === i18n.t('Realogram')) {
        this.viewport.setZoom(bus.sceneZoomScaleValue.realogram, false);
      }

      bus.$emit(EVENTS.ZOOM_END);
    });

    bus.$on(EVENTS.BACKGROUND_ALPHA_CHANGED, (alpha) => {
      this.viewport.removeChild(this.mask);
      this.addMask(alpha);
    });

    bus.$on(EVENTS.SELECTED_IMAGE_NUMBER_CHANGED, async (imageNumber) => {
      this.selectedImageNumber = imageNumber;
    });

    bus.$on(EVENTS.EXPORT_SCENE_IMAGE, () => {
      const canvas = this.renderer.extract.canvas(this.stage);
      const img = canvas.toDataURL('image/jpeg');
      const sceneExportImage = document.createElement('a');
      sceneExportImage.href = img;
      sceneExportImage.download = this.sceneTitle;
      sceneExportImage.style.display = 'none';
      document.body.appendChild(sceneExportImage);
      sceneExportImage.click();
      sceneExportImage.parentNode.removeChild(sceneExportImage);
    });

    bus.$on(EVENTS.FIT_TO_SCREEN, () => {
      if (!this.reference) {
        const zoomFitScale = this.viewport.findFitWidth(this.viewport.worldWidth);

        this.viewport
          .setZoom(zoomFitScale, false)
          .fit(false, this.viewport.worldWidth, this.viewport.worldHeight)
          .moveCenter(this.viewport.worldWidth / 2, this.viewport.worldHeight / 2);
      }
    });

    bus.$on(EVENTS.MOVE_TO_SELECTED_CROP, (payload) => {
      const isCropVisible = this.isSpriteVisible(payload, this.viewport.hitArea);
      if (!this.reference && !isCropVisible) {
        this.viewport.animate({
          time: 300,
          position: { x: payload.global_boundaries.bottom_right.x, y: payload.global_boundaries.bottom_right.y },
          scale: bus.sceneZoomScaleValue.realogram
        });
      }
    });
  }
}
